import {
  BeneficiaryBank,
  CardReissuanceReason,
  InstructionActions,
  ReissuedCardReceivingType,
  Roles,
  WorkingDayAdjustment,
} from "@/modules/application/common/enum";
import { CardSubType, ChangeCardType, Currencies } from "@/shared/common/enums";

interface IOptionsProps {
  t: (s: string) => string;
}

export const currencyOptions = [
  { value: Currencies.AMD, label: Currencies.AMD },
  { value: Currencies.USD, label: Currencies.USD },
  { value: Currencies.EUR, label: Currencies.EUR },
  { value: Currencies.RUB, label: Currencies.RUB },
  { value: Currencies.CHF, label: Currencies.CHF },
  { value: Currencies.GBP, label: Currencies.GBP },
  { value: Currencies.JPY, label: Currencies.JPY },
  { value: Currencies.CAD, label: Currencies.CAD },
  { value: Currencies.AED, label: Currencies.AED },
  { value: Currencies.AUD, label: Currencies.AUD },
  { value: Currencies.CNY, label: Currencies.CNY },
  { value: Currencies.SEK, label: Currencies.SEK },
  { value: Currencies.GEL, label: Currencies.GEL },
];

export const getInstructionActionOptions = ({ t }: IOptionsProps) => ([
  {
    label: t("application.instructionAction.SMSNotificationActivation"),
    value: InstructionActions.SMSNotificationActivation,
  },
  {
    label: t("application.instructionAction.CardUnblockOrActivation"),
    value: InstructionActions.CardUnblockOrActivation,
  },
  {
    label: t("application.instructionAction.CardBlocking"),
    value: InstructionActions.CardBlocking,
  },
  {
    label: t("application.instructionAction.PasswordChange"),
    value: InstructionActions.PasswordChange,
  },
  {
    label: t("application.instructionAction.InstantActivationOfAmount"),
    value: InstructionActions.InstantActivationOfAmount,
  },
  {
    label: t("application.instructionAction.CardReissuance"),
    value: InstructionActions.CardReissuance,
  },
  {
    label: t("application.instructionAction.CardInsurance"),
    value: InstructionActions.CardInsurance,
  },
]);

export const getCardReissuanceReasonOptions = ({ t }: IOptionsProps) => ([
  {
    label: t("application.paymentCardServiceInstruction.PinCodeChange"),
    value: CardReissuanceReason.PinCodeChange,
  },
  {
    label: t("application.paymentCardServiceInstruction.CardRetention"),
    value: CardReissuanceReason.CardRetention,
  },
  {
    label: t("application.paymentCardServiceInstruction.ReissuanceBeforeValidityPeriod"),
    value: CardReissuanceReason.ReissuanceBeforeValidityPeriod,
  },
  {
    label: t("application.paymentCardServiceInstruction.CardDamage"),
    value: CardReissuanceReason.CardDamage,
  },
  {
    label: t("application.paymentCardServiceInstruction.CardLossOrFraud"),
    value: CardReissuanceReason.CardLossOrFraud,
  },
  {
    label: t("application.paymentCardServiceInstruction.CardTypeChange"),
    value: CardReissuanceReason.CardTypeChange,
  }
]);

export const getCardSubTypeOptions = ({ t }: IOptionsProps) => ([
  {
    label: t("general.cardType.ArCaCustomsCard"),
    value: CardSubType.ArCaCustomsCard,
  },
  {
    label: t("general.cardType.MasterBusiness"),
    value: CardSubType.MasterBusiness,
  },
  {
    label: t("general.cardType.VisaBusiness"),
    value: CardSubType.VisaBusiness,
  },
  {
    label: t("general.cardType.VisaBusinessDigital"),
    value: CardSubType.VisaBusinessDigital,
  },
  {
    label: t("general.cardType.MastercardBusinessDigital"),
    value: CardSubType.MastercardBusinessDigital,
  },
  {
    label: t("general.cardType.VisaPlatinumBusiness"),
    value: CardSubType.VisaPlatinumBusiness,
  },
  {
    label: t("general.cardType.VisaPlatinumDigital"),
    value: CardSubType.VisaPlatinumDigital,
  }
]);

export const getChangeCardTypeOptions = ({ t }: IOptionsProps) => ([
  {
    label: t("application.paymentCardServiceInstruction.notApplicable"),
    value: ChangeCardType.NotApplicable,
  },
  {
    label: t("application.paymentCardServiceInstruction.changeCreditToDebit"),
    value: ChangeCardType.ChangeCreditToDebit,
  },
  {
    label: t("application.paymentCardServiceInstruction.changeDebitToCredit"),
    value: ChangeCardType.ChangeDebitToCredit,
  }
]);

export const getReissuedCardReceivingTypeOptions = ({ t }: IOptionsProps) => ([
  {
    label: t("application.paymentCardServiceInstruction.Delivery"),
    value: ReissuedCardReceivingType.Delivery,
  },
  {
    label: t("application.paymentCardServiceInstruction.BankBranch"),
    value: ReissuedCardReceivingType.BankBranch,
  }
]);

export const getDoInshuranceOptions = ({ t }: IOptionsProps) => ([
  {
    label: t("application.paymentCardServiceInstruction.insured"),
    value: true,
  },
  {
    label: t("application.paymentCardServiceInstruction.notInsured"),
    value: false,
  }
]);

export const getRoleOptions = ({ t }: IOptionsProps) => ([
  {
    label: t("application.userOpening.role.viewer"),
    value: Roles.Viewer,
  },
  {
    label: t("application.userOpening.role.editor"),
    value: Roles.Editor,
  },
  {
    label: t("application.userOpening.role.signer1"),
    value: Roles.Signer1,
  },
  {
    label: t("application.userOpening.role.signer2"),
    value: Roles.Signer2,
  }
]);

export const getWorkingDayAdjustmentOptions = ({ t }: IOptionsProps) => ([
  {
    label: t("application.periodicalPayments.workingDayAdjustment.prevWorkingDay"),
    value: WorkingDayAdjustment.PrevWorkingDay,
  },
  {
    label: t("application.periodicalPayments.workingDayAdjustment.noneWorkingDay"),
    value: WorkingDayAdjustment.NoneWorkingDay,
  },
  {
    label: t("application.periodicalPayments.workingDayAdjustment.nextWorkingDay"),
    value: WorkingDayAdjustment.NextWorkingDay,
  }
]);

export const getBeneficiaryBankOptions = ({ t }: IOptionsProps) => ([
  {
    label: t("general.other"),
    value: BeneficiaryBank.Other,
  },
  {
    label: t("bankName.acba"),
    value: BeneficiaryBank.Acba,
  },
  {
    label: t("bankName.ararat"),
    value: BeneficiaryBank.Ararat,
  },
  {
    label: t("bankName.ameria"),
    value: BeneficiaryBank.Ameria,
  },
  {
    label: t("bankName.amio"),
    value: BeneficiaryBank.Amio,
  },
  {
    label: t("bankName.id"),
    value: BeneficiaryBank.IDBank,
  },
  {
    label: t("bankName.ardshin"),
    value: BeneficiaryBank.Ardshin,
  },
  {
    label: t("bankName.armswiss"),
    value: BeneficiaryBank.Armswiss,
  },
  {
    label: t("bankName.artsakh"),
    value: BeneficiaryBank.Artsakh,
  },
  {
    label: t("bankName.byblos"),
    value: BeneficiaryBank.Byblos,
  },
  {
    label: t("bankName.evoca"),
    value: BeneficiaryBank.Evoca,
  },
  {
    label: t("bankName.ineco"),
    value: BeneficiaryBank.Ineco,
  },
  {
    label: t("bankName.converse"),
    value: BeneficiaryBank.Converse,
  },
  {
    label: t("bankName.armeconom"),
    value: BeneficiaryBank.Aeb,
  },
  {
    label: t("bankName.mellat"),
    value: BeneficiaryBank.Mellat,
  },
  {
    label: t("bankName.uni"),
    value: BeneficiaryBank.Unibank,
  },
  {
    label: t("bankName.vtb"),
    value: BeneficiaryBank.VTB,
  },
  {
    label: t("bankName.fast"),
    value: BeneficiaryBank.Fast,
  },
  {
    label: t("general.gov"),
    value: BeneficiaryBank.Gov,
  }
]);
